// The one pricing function (03 §3.3). Used by the office, the ops app, the customer app and the
// server, which always re-prices. Pure: no I/O. All amounts VAT-inclusive (D-18).
//
// The types and the `quote` signature are the Phase 1 contract; the rules are in `quote` below.
import {
  addMoney,
  money,
  roundHalfUp,
  vatFromInclusive,
  type CurrencyCode,
  type Money,
} from './money';
import {
  bestPromotion,
  type AppliedPromotion,
  type PricingPromotion,
  type PromotionSale,
} from './promotions';
import { lineOf } from './service-lines';

export type PricingService = 'sea' | 'air' | 'prime';
const isPricingService = (s: string): s is PricingService =>
  s === 'sea' || s === 'air' || s === 'prime';
/**
 * Air and Prime are charged by the actual weight (owner decision 2026-10-27), so a sale or an
 * added box needs one: the first unit sent without a weight, or undefined. A quote may still use a
 * size's quote weight; a booking may not.
 */
export function firstUnweighed(
  service: string,
  units: readonly { barcode: string; weightKg?: number | undefined }[],
): { barcode: string } | undefined {
  if (service === 'sea') return undefined;
  return units.find((u) => u.weightKg === undefined);
}

export type DestinationKind = 'city' | 'route' | 'zone' | 'branch' | 'country';

export interface PricingRule {
  id: string;
  destination?: { kind: DestinationKind; value: string };
  sizeId?: string;
  basis: 'per_box' | 'per_kg' | 'per_cbm';
  rateMinor: number;
  minChargeMinor?: number;
  volumetricDivisor?: number;
  /**
   * per_kg only: a weight bracket by chargeable kg, `minKg` inclusive and `maxKg` exclusive (after
   * rounding up to 0.5 kg and after `minChargeableKg`). "10–29 kg" and "30 kg and up" are
   * {minKg: 10, maxKg: 30} and {minKg: 30}; a 29.5 kg box is in the first.
   */
  minKg?: number;
  maxKg?: number;
  /** per_kg only: the weight charged at least, e.g. 10 for "minimum 10 kg". */
  minChargeableKg?: number;
}

export interface PricingAddOn {
  code: string;
  label: string;
  kind: 'percent_of_declared' | 'per_shipment' | 'per_box';
  value: number;
  minChargeMinor?: number;
}

/** The parts of a rate card pricing needs (the full document schema is in @ecobox/schema). */
export interface PricingCard {
  id: string;
  version: number;
  service: PricingService;
  currency: CurrencyCode;
  rules: readonly PricingRule[];
  addOns: readonly PricingAddOn[];
  minimumChargeMinor?: number;
}

/** Where the shipment is going, resolved from the destination city and coverage. */
export interface PricingDestination {
  cityId: string;
  routeCode?: string;
  zoneId?: string;
  branchId?: string;
  country: string;
}

export interface PricingBox {
  /** Barcode or a temporary key while quoting. */
  key: string;
  sizeId: string;
  /** Actual weight; when absent the size's quote weight is used (quotes only). */
  weightKg?: number;
  dimsCm?: { l: number; w: number; h: number };
}

export interface QuoteInput {
  /** Cards in precedence order: the origin branch's card first, then the origin country's card. */
  cards: readonly PricingCard[];
  destination: PricingDestination;
  boxes: readonly PricingBox[];
  /** Size definitions for quote weights and standard dimensions. */
  sizes: ReadonlyMap<
    string,
    { quoteWeightKg?: number; dimsCm?: { l: number; w: number; h: number } }
  >;
  declaredValueMinor?: number;
  addOnCodes?: readonly string[];
  /** A fixed discount (voucher or staff discount), VAT-inclusive. It comes after any promotion. */
  discountMinor?: number;
  vatRatePercent: number;
  /** Promotions to try (./promotions.ts), with the sale they are checked against. */
  promotions?: readonly PricingPromotion[];
  sale?: Omit<PromotionSale, 'currency' | 'freight' | 'destinationCountry'>;
}

export interface QuoteLine {
  kind: 'freight' | 'addon' | 'discount';
  label: string;
  boxKey?: string;
  amount: Money;
  /** Which card and rule priced a freight line (the rate snapshot, D-09). */
  cardId?: string;
  cardVersion?: number;
  ruleId?: string;
  /** For per_kg / per_cbm lines: the chargeable quantity used. */
  quantity?: number;
  /**
   * A box's freight line: what the price is based on, in plain words, e.g. "Charged as a size M
   * box" or "Chargeable 12.5 kg: volumetric weight of its measurements".
   */
  basis?: string;
  /** A promotion's discount line: which promotion gave it. */
  promotionId?: string;
}

export interface QuoteResult {
  ok: true;
  currency: CurrencyCode;
  lines: QuoteLine[];
  /** VAT-inclusive total, then its split. */
  gross: Money;
  net: Money;
  vat: Money;
  /** The promotion that priced this quote, when one applied. */
  promotion?: AppliedPromotion;
}

export interface QuoteFailure {
  ok: false;
  /** Plain words, e.g. "No rate for size M to Cebu City on any card". */
  reason: string;
  boxKey?: string;
}

// ---------------------------------------------------------------- resolution

const DESTINATION_RANK: Record<DestinationKind, number> = {
  city: 5,
  route: 4,
  zone: 3,
  branch: 2,
  country: 1,
};

function destinationValue(d: PricingDestination, kind: DestinationKind): string | undefined {
  switch (kind) {
    case 'city':
      return d.cityId;
    case 'route':
      return d.routeCode;
    case 'zone':
      return d.zoneId;
    case 'branch':
      return d.branchId;
    case 'country':
      return d.country;
  }
}

/** True when a per_kg rule's weight bracket takes this box (a rule with no bracket takes any). */
function inBracket(rule: PricingRule, box: PricingBox, size: SizeInfo | undefined): boolean {
  if (rule.minKg === undefined && rule.maxKg === undefined) return true;
  if (rule.basis !== 'per_kg') return false;
  const halves = chargeableHalves(rule, box, size)?.halves;
  if (halves === undefined) return false;
  const kg = halves / 2;
  return (
    (rule.minKg === undefined || kg >= rule.minKg) && (rule.maxKg === undefined || kg < rule.maxKg)
  );
}

/**
 * Destination specificity first (city … country, then none), a size-specific rule second, and a
 * rule whose weight bracket takes the box is preferred over one with no bracket.
 */
function ruleScore(
  rule: PricingRule,
  box: PricingBox,
  d: PricingDestination,
  size: SizeInfo | undefined,
): number | undefined {
  if (rule.sizeId !== undefined && rule.sizeId !== box.sizeId) return undefined;
  if (!inBracket(rule, box, size)) return undefined;
  let destinationRank = 0;
  if (rule.destination) {
    if (destinationValue(d, rule.destination.kind) !== rule.destination.value) return undefined;
    destinationRank = DESTINATION_RANK[rule.destination.kind];
  }
  const bracketed = rule.minKg !== undefined || rule.maxKg !== undefined;
  return destinationRank * 4 + (rule.sizeId === undefined ? 0 : 2) + (bracketed ? 1 : 0);
}

interface Match {
  card: PricingCard;
  rule: PricingRule;
}

/** The first card with any matching rule wins; inside it the best score wins, the earlier rule on a tie. */
function findRule(
  cards: readonly PricingCard[],
  box: PricingBox,
  d: PricingDestination,
  size: SizeInfo | undefined,
): Match | undefined {
  for (const card of cards) {
    let best: { rule: PricingRule; score: number } | undefined;
    for (const rule of card.rules) {
      const score = ruleScore(rule, box, d, size);
      if (score !== undefined && (best === undefined || score > best.score)) best = { rule, score };
    }
    if (best) return { card, rule: best.rule };
  }
  return undefined;
}

/**
 * True when some rule of some card could price a box to this destination (a box size may still be
 * missing). The website uses it to say whether a service is offered for a city.
 */
export function cardsCoverDestination(
  cards: readonly PricingCard[],
  d: PricingDestination,
): boolean {
  return cards.some((card) =>
    card.rules.some(
      (rule) =>
        rule.destination === undefined ||
        destinationValue(d, rule.destination.kind) === rule.destination.value,
    ),
  );
}

// ------------------------------------------------------------------ charges

type Dims = { l: number; w: number; h: number };
type SizeInfo = { quoteWeightKg?: number; dimsCm?: Dims };
type Charge = { amountMinor: number; quantity?: number; basis: string } | { error: string };

/** Centimetres in hundredths, so volumes are exact integers. */
function hundredths(dims: Dims): { l: bigint; w: bigint; h: bigint } {
  const h = (n: number) => BigInt(Math.round(n * 100));
  return { l: h(dims.l), w: h(dims.w), h: h(dims.h) };
}

function ceilDiv(n: bigint, d: bigint): bigint {
  return (n + d - 1n) / d;
}

/** Weight in half-kilograms, rounded up. */
function halfKgOf(kg: number): bigint {
  return ceilDiv(BigInt(Math.round(kg * 1000)), 500n);
}

/** Volumetric weight in half-kilograms, rounded up. */
function volumetricHalfKg(dims: Dims, divisor: number): bigint {
  const { l, w, h } = hundredths(dims);
  // kg = l*w*h cm³ / divisor, and l*w*h cm³ = (l100*w100*h100) / 1e6.
  return ceilDiv(2n * l * w * h, 1_000_000n * BigInt(divisor));
}

const volume = (d: Dims): number => d.l * d.w * d.h;

/**
 * The measurements a box is priced by. A standard size is never priced smaller than the size
 * itself: measurements that make it smaller (a Small box declared at 30 cm a side) are ignored,
 * larger ones count. A size with no standard measurements (odd size) is priced as measured.
 */
function pricedDims(box: PricingBox, size: SizeInfo | undefined): Dims | undefined {
  if (box.dimsCm && size?.dimsCm)
    return volume(box.dimsCm) >= volume(size.dimsCm) ? box.dimsCm : size.dimsCm;
  return box.dimsCm ?? size?.dimsCm;
}

/** Where a per_kg box's chargeable weight came from (`weightBasis` words it). */
type WeightSource =
  'actual' | 'quote_weight' | 'volumetric_measured' | 'volumetric_size' | 'minimum';

/**
 * A per_kg rule's chargeable half-kilograms for a box: the larger of actual and volumetric,
 * rounded up to 0.5 kg, raised to the rule's minimum weight; with where the winner came from. On a
 * tie the actual weight is named, then the volumetric.
 */
function chargeableHalves(
  rule: PricingRule,
  box: PricingBox,
  size: SizeInfo | undefined,
): { halves: number; from: WeightSource } | undefined {
  const candidates: { halves: bigint; from: WeightSource }[] = [];
  const actualKg = box.weightKg ?? size?.quoteWeightKg;
  if (actualKg !== undefined) {
    candidates.push({
      halves: halfKgOf(actualKg),
      from: box.weightKg === undefined ? 'quote_weight' : 'actual',
    });
  }
  const dims = pricedDims(box, size);
  if (dims && rule.volumetricDivisor) {
    candidates.push({
      halves: volumetricHalfKg(dims, rule.volumetricDivisor),
      from: dims === box.dimsCm ? 'volumetric_measured' : 'volumetric_size',
    });
  }
  const minimum = BigInt(Math.ceil((rule.minChargeableKg ?? 0) * 2));
  if (candidates.length === 0) return undefined;
  candidates.push({ halves: minimum, from: 'minimum' });
  const best = candidates.reduce((a, b) => (b.halves > a.halves ? b : a));
  return { halves: Number(best.halves), from: best.from };
}

const kgText = (kg: number): string => `${kg} kg`;

/** Plain words for what a per_kg box's chargeable weight is (QuoteLine.basis). */
function weightBasis(rule: PricingRule, box: PricingBox, kg: number, from: WeightSource): string {
  const why: Record<WeightSource, string> = {
    actual: 'its actual weight',
    quote_weight: `the usual weight of a size ${box.sizeId} box (no weight entered)`,
    volumetric_measured: 'volumetric weight of its measurements',
    volumetric_size: `volumetric weight of a standard size ${box.sizeId} box`,
    minimum: `the minimum of ${kgText(rule.minChargeableKg ?? 0)}`,
  };
  return `Chargeable ${kgText(kg)}: ${why[from]}`;
}

function chargeBox(rule: PricingRule, box: PricingBox, size: SizeInfo | undefined): Charge {
  const dims = pricedDims(box, size);
  switch (rule.basis) {
    case 'per_box':
      return { amountMinor: rule.rateMinor, basis: `Charged as a size ${box.sizeId} box` };
    case 'per_kg': {
      const chargeable = chargeableHalves(rule, box, size);
      if (chargeable === undefined) {
        return { error: `needs a weight (or measurements) to price per kg, and none is known` };
      }
      const kg = chargeable.halves / 2;
      return {
        amountMinor: roundHalfUp(BigInt(rule.rateMinor) * BigInt(chargeable.halves), 2),
        quantity: kg,
        basis: weightBasis(rule, box, kg, chargeable.from),
      };
    }
    case 'per_cbm': {
      if (!dims)
        return { error: 'needs measured length, width and height to price per cubic metre' };
      const { l, w, h } = hundredths(dims);
      const cm3x1e6 = l * w * h;
      const cbm = Number(cm3x1e6) / 1e12;
      return {
        amountMinor: roundHalfUp(BigInt(rule.rateMinor) * cm3x1e6, 1_000_000_000_000n),
        quantity: cbm,
        basis: `Charged by volume: ${Number(cbm.toFixed(3))} m³${dims === box.dimsCm ? '' : ` (a standard size ${box.sizeId} box)`}`,
      };
    }
  }
}

function addOnAmount(addOn: PricingAddOn, declaredMinor: number, boxCount: number): number {
  let amount: number;
  switch (addOn.kind) {
    case 'percent_of_declared':
      // value is a percent with up to 4 decimals (1 = 1%).
      amount = roundHalfUp(
        BigInt(declaredMinor) * BigInt(Math.round(addOn.value * 10000)),
        1_000_000,
      );
      break;
    case 'per_shipment':
      amount = Math.round(addOn.value);
      break;
    case 'per_box':
      amount = Math.round(addOn.value) * boxCount;
      break;
  }
  return Math.max(amount, addOn.minChargeMinor ?? 0);
}

// --------------------------------------------------------------------- quote

function fail(reason: string, boxKey?: string): QuoteFailure {
  return boxKey === undefined ? { ok: false, reason } : { ok: false, reason, boxKey };
}

function describeBox(box: PricingBox, d: PricingDestination): string {
  return `size ${box.sizeId} to ${d.cityId}`;
}

function isMinor(n: number): boolean {
  return Number.isSafeInteger(n) && n >= 0;
}

function validate(input: QuoteInput): QuoteFailure | undefined {
  if (input.boxes.length === 0) return fail('There are no boxes to price');
  if (input.cards.length === 0) return fail('There is no rate card for this origin and service');
  if (
    !Number.isFinite(input.vatRatePercent) ||
    input.vatRatePercent < 0 ||
    input.vatRatePercent > 100
  ) {
    return fail(`The VAT rate ${input.vatRatePercent} is not valid`);
  }
  if (input.declaredValueMinor !== undefined && !isMinor(input.declaredValueMinor)) {
    return fail('The declared value must be a whole number of minor units, zero or more');
  }
  if (input.discountMinor !== undefined && !isMinor(input.discountMinor)) {
    return fail('The discount must be a whole number of minor units, zero or more');
  }
  return undefined;
}

/**
 * Prices a shipment. Resolution per box (03 §3.3): on each card in order, the most specific
 * destination match wins (city → route → zone → branch → country → no destination), and a
 * size-specific rule beats an any-size rule. No match on any card is a failure: never a
 * hard-coded fallback price.
 *
 * Per-box charge: per_box = rate; per_kg = rate × chargeable kg (max of actual and volumetric,
 * rounded up to 0.5 kg, then at least the rule's `minChargeableKg`; a rule with a weight bracket
 * prices only the boxes whose chargeable kg falls in it); per_cbm = rate × l×w×h / 1e6 m³. The rule's minimum applies per box.
 * Each box's line says in plain words what it was charged by (`basis`), so the counter can show it.
 * Add-ons come from the card that priced the first box. The card's minimum applies to the
 * freight total as a top-up line. A discount is a negative line and never takes the total below zero.
 * All boxes must price in one currency. Money maths is exact integer (bigint) and rounds half-up once per line.
 */
export function quote(input: QuoteInput): QuoteResult | QuoteFailure {
  const invalid = validate(input);
  if (invalid) return invalid;
  const d = input.destination;

  const priced: {
    box: PricingBox;
    match: Match;
    charge: { amountMinor: number; quantity?: number; basis: string };
  }[] = [];
  for (const box of input.boxes) {
    const match = findRule(input.cards, box, d, input.sizes.get(box.sizeId));
    if (!match) return fail(`No rate for ${describeBox(box, d)} on any rate card`, box.key);
    const charge = chargeBox(match.rule, box, input.sizes.get(box.sizeId));
    if ('error' in charge)
      return fail(`Box ${box.key} (${describeBox(box, d)}) ${charge.error}`, box.key);
    const floored = Math.max(charge.amountMinor, match.rule.minChargeMinor ?? 0);
    const basis =
      floored > charge.amountMinor
        ? `${charge.basis}; raised to the rule's minimum charge`
        : charge.basis;
    priced.push({ box, match, charge: { ...charge, amountMinor: floored, basis } });
  }

  const first = priced[0] as (typeof priced)[number];
  const currency = first.match.card.currency;
  const mixed = priced.find((p) => p.match.card.currency !== currency);
  if (mixed) {
    return fail(
      `Boxes price in different currencies (${currency} and ${mixed.match.card.currency}); quote them as separate shipments`,
      mixed.box.key,
    );
  }

  const lines: QuoteLine[] = priced.map(({ box, match, charge }) => ({
    kind: 'freight',
    label: `Box ${box.sizeId} to ${d.cityId}`,
    boxKey: box.key,
    amount: money(charge.amountMinor, currency),
    cardId: match.card.id,
    cardVersion: match.card.version,
    ruleId: match.rule.id,
    ...(charge.quantity === undefined ? {} : { quantity: charge.quantity }),
    basis: charge.basis,
  }));

  const boxSize = new Map(input.boxes.map((b) => [b.key, b.sizeId]));
  const card = first.match.card;
  const freight = priced.reduce((sum, p) => sum + p.charge.amountMinor, 0);
  const topUp = (card.minimumChargeMinor ?? 0) - freight;
  if (topUp > 0) {
    lines.push({
      kind: 'freight',
      label: 'Minimum charge top-up',
      amount: money(topUp, currency),
      cardId: card.id,
      cardVersion: card.version,
    });
  }

  for (const code of new Set(input.addOnCodes ?? [])) {
    const addOn = card.addOns.find((a) => a.code === code);
    if (!addOn) return fail(`The add-on "${code}" is not offered on rate card ${card.id}`);
    const amount = addOnAmount(addOn, input.declaredValueMinor ?? 0, input.boxes.length);
    lines.push({ kind: 'addon', label: addOn.label, amount: money(amount, currency) });
  }

  const subtotal = lines.reduce((sum, l) => addMoney(sum, l.amount), money(0, currency));
  const promotion =
    input.promotions && input.sale
      ? bestPromotion(input.promotions, {
          ...input.sale,
          // The line the server stores when none is chosen (D-28), so a device and the server agree.
          serviceLine:
            input.sale.serviceLine ??
            (isPricingService(input.sale.service)
              ? lineOf({ service: input.sale.service, destinationCountry: d.country })
              : undefined),
          currency,
          destinationCountry: d.country,
          freight: lines
            .filter((l) => l.kind === 'freight')
            .map((l) => ({
              sizeId: l.boxKey === undefined ? undefined : boxSize.get(l.boxKey),
              amountMinor: l.amount.amountMinor,
            })),
        })
      : undefined;
  const promo = promotion?.discountMinor ?? 0;
  if (promotion) {
    lines.push({
      kind: 'discount',
      label: `Promo: ${promotion.label}`,
      amount: money(-promo, currency),
      promotionId: promotion.id,
    });
  }
  const discount = Math.min(input.discountMinor ?? 0, subtotal.amountMinor - promo);
  if (discount > 0) {
    lines.push({ kind: 'discount', label: 'Discount', amount: money(-discount, currency) });
  }

  const gross = money(subtotal.amountMinor - promo - discount, currency);
  const split = vatFromInclusive(gross, Number(input.vatRatePercent.toFixed(6)));
  return {
    ok: true,
    currency,
    lines,
    gross,
    net: split.net,
    vat: split.vat,
    ...(promotion ? { promotion } : {}),
  };
}
